import React, { useState } from "react";
import { s3baseUri } from "../../config/config";
import { ArrowRight, ExternalLink } from "lucide-react";
import {
  addCommasToNumber,
  handle_get_time_ago,
  removeExtraSpacesFromHTML,
} from "../../utils/Constant";
import { useIds } from "../../hook/AdminContext";
import { IconButton } from "@mui/material";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import CustomPopoverSection from "../menu/MebuCustomPopOverSection";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CompanyPopup from "../popUp/CompanyPopup";
import {
  clock_gray_icon,
  map_gray_icon,
  noImagePlaceholder,
  salary_gray_icon,
  three_dots_icon,
} from "../../assets";
import QuickApplyModal from "../QuickApply/QuickApplyModal";
import JobDescriptionBlock from "./components/JobDescriptionBlock";
import { useNavigate } from "react-router-dom";
import ActionByChip from "../cards/ActionByChip";

export default function JobListCard({
  job,
  handlePageMove = () => {},
  isTablet = false,
  handleBookmarkToggle = () => {},
  handleCandidates = () => {},
  MENU_OPTIONS = () => {},
  isDetailClient = false,
  fetchDetails = () => {},
  setJobsList = () => {},
}) {
  /* =========================
     STATE
  ========================= */
  const { userInfo, handleGetActionByStatus } = useIds();
  const navigate = useNavigate();
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [companyModalData, setCompanyModalData] = useState(false);
  const [openQuickApply, setOpenQuickApply] = useState(false);

  /* =========================
     DERIVED DATA & FLAGS
  ========================= */
  const isOwner = job?.created_by?._id === userInfo?.profile_id;
  const isButtonAllow = userInfo?.role === "candidate" || isOwner;

  const companyImage =
    job?.created_by?.profile_image?.thumbnail_2 ||
    job?.created_by?.profile_image?.thumbnail_1
      ? s3baseUri +
        (job?.created_by?.profile_image?.thumbnail_2 ||
          job?.created_by?.profile_image?.thumbnail_1)
      : null;

  const companyInitial =
    !companyImage && job?.created_by?.company_name
      ? job.created_by.company_name.charAt(0)
      : null;

  const jobStatusBadge = !job.status ? "Inactive" : null;

  const showBookmark =
    userInfo?.role === "candidate" && !userInfo?.isCompanyProfile;

  const jobTypeTitle = job.job_type?.title || "";

  const formattedSalary =
    job.salary_type === "fixed"
      ? `€${addCommasToNumber(job.salary)}`
      : `€${addCommasToNumber(job.min_salary)} - €${addCommasToNumber(
          job.max_salary
        )}`;

  const salaryFrequency = job.frequency_salary
    ? {
        Monthly: "Month",
        Daily: "Day",
        Weekly: "Week",
        Hourly: "Hour",
        Anually: "Annually",
      }[job.frequency_salary] || job.frequency_salary
    : "";

  const actionByName =
    job?.action_by?.company_name ||
    `${job?.action_by?.first_name || ""} ${job?.action_by?.last_name || ""}`;

  const timeAgo = handle_get_time_ago(job.createdAt);

  const menuOptions =
    MENU_OPTIONS(job).length > 0
      ? MENU_OPTIONS(job).map((option) => ({
          ...option,
          disabled: option.getDisabled ? option.getDisabled(job) : false,
          tooltip: option.getTooltip ? option.getTooltip(job) : "",
        }))
      : [];

  const hasApplied = job.applied_status;

  /* =========================
     HANDLERS
  ========================= */
  const handleCompanyNameClick = (Job, e) => {
    e.preventDefault();
    e.stopPropagation();
    // setCompanyModalData(Job?.created_by);
    // setCompanyModalOpen(true);
    navigate("/user/" + Job?.created_by._id);
  };

  /* =========================
     JSX
  ========================= */

  const job_name_and_company = (
    <div className="tw-w-full">
      <div
        className={`job-heading-block d-flex justify-content-between tw-flex-shrink-0 ${
          isTablet ? "tw-ms-4 tw-mt-0" : "tw-ms-0 tw-mt-4"
        }`}
      >
        <div>
          <h6 className={`job-card-title mb-1 ${isTablet ? "text-xl" : ""}`}>
            {job.job_title}
          </h6>
          <p
            className="tw-job-company a-link"
            onClick={(e) => handleCompanyNameClick(job, e)}
            style={{ pointerEvents: isDetailClient ? "none" : "" }}
          >
            {job?.created_by?.company_name}
          </p>
        </div>
      </div>
    </div>
  );

  const badges_and_edit = (
    <div className="text-end">
      <div className="d-flex gap-1 justify-content-end">
        {isTablet && (
          <>
            {isOwner && <div className="job-green-chip">My Job</div>}
            <span className="job-yellow-chip">{jobTypeTitle}</span>

            {jobStatusBadge && (
              <span className="job-red-chip">{jobStatusBadge}</span>
            )}
          </>
        )}
        {!isTablet && (
          <>
            {jobStatusBadge && (
              <span className="job-type-badge error-badge d-block tw-mr-2">
                {jobStatusBadge}
              </span>
            )}
            <span className="job-type-badge">{jobTypeTitle}</span>
          </>
        )}

        {showBookmark && (
          <IconButton
            sx={{
              padding: "0px",
              paddingInlineStart: "4px !important",
              fontSize: "10px !important",
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleBookmarkToggle(job._id);
            }}
          >
            {job.is_favourite ? (
              <BookmarkIcon
                className="responsive-icon"
                sx={{
                  color: "var(--portal-primary-color)",
                }}
              />
            ) : (
              <BookmarkBorderIcon className="responsive-icon" />
            )}
          </IconButton>
        )}

        {menuOptions.length > 0 && (
          <CustomPopoverSection
            menu={menuOptions}
            data={job}
            custom_icon={three_dots_icon}
          />
        )}
      </div>

      {handleGetActionByStatus(job, "job") && (
        <ActionByChip
          actionByName={actionByName}
          className={
            isTablet
              ? "new-chip new-chip--outlined tw-text-[14px] tw-leading-[16px] tw-h-[30px] mt-2"
              : "new-chip new-chip--outlined tw-text-[10px] tw-leading-[16px] tw-h-[20px] mt-2"
          }
        />
      )}
    </div>
  );
  return (
    <>
      {companyModalOpen && (
        <CompanyPopup
          companyModalOpen={companyModalOpen}
          handleCloseCompanyModal={() => setCompanyModalOpen(false)}
          companyModalData={companyModalData}
        />
      )}

      {openQuickApply && (
        <QuickApplyModal
          open={openQuickApply}
          setOpen={setOpenQuickApply}
          targetJob={job}
          fetchDetails={fetchDetails}
          setJobsList={setJobsList}
        />
      )}

      <div
        key={job._id}
        className={`job-listings ${"tw-cursor-pointer"} card-1 p-3 mb-3 ${
          isTablet ? "job-lg-view" : ""
        }`}
        onClick={(e) => {
          e.stopPropagation();
          handlePageMove(job);
        }}
      >
        {isOwner && !isTablet && <div className="my-job-label">My Job</div>}

        {/* Job Header */}
        <div className={`job-tw-header d-flex justify-content-between`}>
          <div className="d-flex justify-content-between align-items-center">
            <div className={`tw-job-logo`}>
              {companyImage ? (
                <img
                  className="img-fluid rounded w-100 h-100 tw-cursor-pointer"
                  src={companyImage}
                  onClick={(e) => handleCompanyNameClick(job, e)}
                  onError={(e) => {
                    e.target.src = noImagePlaceholder;
                  }}
                  alt={job?.creater_info?.company_name + " Logo"}
                  style={{
                    objectFit: "cover",
                    pointerEvents: isDetailClient ? "none" : "",
                  }}
                />
              ) : (
                companyInitial
              )}
            </div>
            {/* it will show on the large job card */}
            {isTablet && <div>{job_name_and_company}</div>}
          </div>
          {/* badges and edit here */}
          {badges_and_edit}
        </div>
        {/* it will show on the small cards */}
        <div>{!isTablet && <div>{job_name_and_company}</div>}</div>

        {/* Job meta */}
        <div className={isTablet ? "py-3 border-bottom" : "pb-1 border-bottom"}>
          <div className={isTablet ? "row" : ""}>
            <div className={isTablet ? "col-12 col-lg-3" : ""}>
              <div className="job-meta-item">
                <img src={clock_gray_icon} className="job-meta-icon" />
                <span className="job-meta-text">{timeAgo}</span>
              </div>
            </div>
            <div className={isTablet ? "col-12 col-lg-3" : ""}>
              <div className="job-meta-item">
                <img
                  src={salary_gray_icon}
                  alt="salary_gray_icon"
                  className="job-meta-icon"
                />
                <span className="job-meta-text">
                  {formattedSalary}
                  {salaryFrequency && ` / ${salaryFrequency}`}
                </span>
              </div>
            </div>
            <div className={isTablet ? "col-12 col-lg-6" : ""}>
              <div className="job-meta-item">
                <img
                  src={map_gray_icon}
                  alt="map_gray_icon"
                  className="job-meta-icon"
                />
                <span className="job-meta-text">{job.location}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="mt-3">
          <p className="tinymce-reset fs-14">
            <span
              className="desc-text tw-line-clamp-3"
              dangerouslySetInnerHTML={{
                __html: removeExtraSpacesFromHTML(job.description || ""),
              }}
            />
          </p>
        </div>

        {/* Buttons */}
        {isButtonAllow && (
          <div
            className={`${
              (isOwner &&
                (userInfo?.role === "client" || userInfo?.isCompanyProfile)) ||
              showBookmark
                ? "pb-5"
                : ""
            }`}
          >
            <div className="job-actions">
              {isOwner &&
                (userInfo?.role === "client" || userInfo?.isCompanyProfile) && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCandidates(job);
                    }}
                    className="btn-contained-sm tw-group position-relative"
                  >
                    Candidates
                    <ExternalLink className="tw-h-4 tw-w-4 group-hover:tw-translate-x-1 tw-transition-transform" />
                    <div
                      className="badge position-absolute"
                      style={{
                        top: "-7px",
                        right: "-4px",
                        backgroundColor: "#FF0000",
                        color: "#fff",
                        padding: "2px 6px",
                        borderRadius: "12px",
                        minWidth: "20px",
                        textAlign: "center",
                        fontSize: "12px",
                        lineHeight: "1.2",
                      }}
                    >
                      {job?.applicant_count || 0}
                    </div>
                  </button>
                )}

              {showBookmark &&
                (hasApplied ? (
                  <button
                    className="btn-success-sm tw-group"
                    aria-disabled="true"
                    disabled={true}
                    onClick={(e) => e.stopPropagation()}
                  >
                    Applied{" "}
                    <CheckCircleOutlineIcon
                      style={{ marginRight: "4px", fontSize: "18px" }}
                    />
                  </button>
                ) : (
                  <div className="d-flex justify-content-end tw-gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenQuickApply(true);
                      }}
                      className={`btn-contained-sm tw-group ${
                        !isTablet ? "!tw-px-3" : ""
                      }`}
                    >
                      Quick Apply{" "}
                      <ExternalLink className="tw-h-4 tw-w-4 group-hover:tw-translate-x-1 tw-transition-transform" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePageMove(job);
                      }}
                      className={`btn-outlined-sm tw-group ${
                        !isTablet ? "!tw-px-3" : ""
                      }`}
                    >
                      View Details{" "}
                      <ArrowRight className="tw-h-4 tw-w-4 group-hover:tw-translate-x-1 tw-transition-transform" />
                    </button>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
